import { useQuery } from '@tanstack/react-query';

import {
  getRequest,
  paginationMapper,
  resolveQueryString,
} from 'src/utils/request';
import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { resolveDataFilter } from 'src/components/SearchFilterBar';

import type { RequestDataArray } from 'src/types/requestData';
import type { AdvancedFiltersState } from 'src/components/AdvancedFilters';

const mapProjects = (
  project: UCM.ProjectRawType,
  projectId: string,
): UCM.ProjectType => {
  return {
    id: projectId,
    uuid: project.uuid,
    name: project.name,
    endDate: project.end_date,
    startDate: project.start_date,
    cities: project.unique_cities,
    statusName: project.status_name,
    isPriority: project.is_priority,
    hasFlexJobs: project.has_flex_jobs,
    customerName: project.customer_name,
    hasStandbyJobs: project.has_standby_jobs,
    slotsCount: project.total_published.slots_count,
    bookedStaffCount: project.total_published.booked_staff_count,
  };
};

const getProjects = async <T extends UCM.ProjectStatusType>(
  projectStatus: T,
  filters: Array<AdvancedFiltersState> = [],
  params: Record<string, string | number>,
) => {
  const updatedFilters =
    projectStatus === 'open'
      ? [
          ...filters,
          ...resolveDataFilter([
            { name: 'Status', operator: 'in', value: ['1', '3'] },
          ]),
        ]
      : filters;
  const query = resolveQueryString([updatedFilters], params);

  const {
    data: { data, meta },
  } = await getRequest<RequestDataArray<UCM.ProjectRawType, UCM.MetaRawType>>(
    `${API.GET.getAllProjects}${query}`,
  );

  return {
    meta: paginationMapper(meta),
    data: data.map(({ attributes, id }) => mapProjects(attributes, id)),
  };
};

export const useGetProjects = (
  projectStatus: UCM.ProjectStatusType,
  params: Record<string, string | number>,
  filters: Array<AdvancedFiltersState> = [],
  config: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PROJECTS, projectStatus, filters, params],
    queryFn: () => getProjects(projectStatus, filters, params),
    staleTime: 1000 * 60 * 5, // Dashboards refreshed after every 5 minutes automatically
    ...config,
  });
};
